import EventDispatcher from '../event/event-dispatcher';
import GeneralUtility from './general-utility';
import CookieUtility from './cookie-utility';

export interface Settings {
  version: number, // Version of the settings. If changed and the users version of the settings in the cookie do not fit, the saved user settings are reset.
  languageId: number, // ID of the language when the settings were saved. Can be used to identify if settings should be reset after language change.
  plugins: {
    // Key is a plugin name
    [key: string]: any
  }
}

/**
 * Settings for a user.
 *
 * @author Philipp Seiler <ps@typoheads.at>
 */
export default class UserSettings {
  // Version of the settings. If changed and the users version of the settings in the cookie do not fit, the settings are reset.
  static readonly VERSION: number = 5;

  // Singleton instance
  private static _instance: UserSettings;

  // Key for the cookie to save the settings within
  private _key: string = 'sitepackage';

  // Contains the current settings
  private _settings: Settings;

  // Event for checking if the settings have changed
  private _changeEvent = new EventDispatcher<Settings>();

  static getInstance(): UserSettings {
    if (UserSettings._instance === undefined) {
      UserSettings._instance = new UserSettings();
    }
    return UserSettings._instance;
  }

  private constructor() {
    this.settings = this.get();
  }

  /**
   * Gets the key of the cookie where settings are saved within.
   *
   * @returns {string}
   */
  private get key(): string {
    return this._key;
  }

  /**
   * Gets the current settings.
   *
   * @returns {Settings}
   */
  private get settings(): Settings {
    return this._settings;
  }

  /**
   * Sets new current settings.
   *
   * @param {Settings} settings New settings
   */
  private set settings(settings: Settings) {
    this._settings = settings;
  }

  /**
   * Gets the event for checking if the settings have changed.
   *
   * @returns {EventDispatcher<Settings>}
   */
  get changeEvent(): EventDispatcher<Settings> {
    return this._changeEvent;
  }

  /**
   * Gets the settings from the cookie.
   *
   * @returns {Settings}
   */
  get(): Settings {
    let settings: Settings = null;

    // No settings exist yet
    if (!CookieUtility.exists(this.key)) {
      settings = this.reset();
    }

    // Try to load existing settings
    else {
      try {
        settings = JSON.parse(CookieUtility.get(this.key));

        // Sanity check
        if (!settings.hasOwnProperty('version')) {
          settings = this.reset();
        }
      } catch (e) {
        console.error(e);
        settings = this.reset();
      }
    }

    // If no settings could be retrieved, throw an error
    if (settings === null) {
      throw new Error('Could not load settings.');
    }

    // Check if the settings version is equal to the current version. If no, reset the settings.
    if (settings.version !== UserSettings.VERSION) {
      settings = this.reset();
    }

    // If the language changed between saved settings, reset all settings
    if (settings.languageId !== GeneralUtility.languageId) {
      settings = this.reset();
    }

    return settings;
  }

  /**
   * Sets new settings and saves them in the cookie.
   *
   * @param {Settings} settings New settings
   */
  set(settings: Settings): void {
    if (!CookieUtility.exists(this.key)) {
      this.reset();
    }
    this.save(settings);
    this.settings = settings;
    this.changeEvent.dispatch(this.settings);
  }

  /**
   * Gets default settings.
   *
   * @returns {Settings}
   */
  getDefaultSettings(): Settings {
    return {
      'version': UserSettings.VERSION,
      'languageId': GeneralUtility.languageId,
      'plugins': {}
    };
  }

  /**
   * Resets user settings and saves them in the cookie.
   *
   * @returns {Settings} New settings
   */
  reset(): Settings {
    const settings = this.getDefaultSettings();
    this.save(settings);
    this.settings = settings;
    this.changeEvent.dispatch(this.settings);
    return settings;
  }

  /**
   * Persists the user settings in the cookie.
   *
   * @param {Settings} settings Settings to persist
   */
  private save(settings: Settings): void {
    CookieUtility.set(this.key, JSON.stringify(settings), this.getCookieExpirationDate());
  }

  /**
   * Refreshes the user settings, meaning they will be re-saved as-is and their cookie expiration date will be updated.
   */
  public refresh(): void {
    CookieUtility.set(this.key, JSON.stringify(this.get()), this.getCookieExpirationDate());
  }

  /**
   * Gets the cookie expiration date.
   *
   * @returns {Date}
   */
  private getCookieExpirationDate(): Date {
    const now = new Date();
    return new Date(now.getTime() + 60000 * 5);
  }

  /**
   * Gets the settings tied to a specific plugin.
   *
   * @param {string} name Name of the plugin to get settings for
   * @returns {any}
   */
  getPluginSettings(name: string): any {
    // Initialize empty settings if settings do not exist yet for the given plugin
    if (!this.settings.plugins.hasOwnProperty(name)) {
      this.resetPluginSettings(name);
    }

    return this.settings.plugins[name];
  }

  /**
   * Sets the settings for a specific plugin.
   *
   * @param {string} name Name of the plugin to set settings for
   * @param {any} settings Filters to set
   */
  setPluginSettings(name: string, settings: any): void {
    this.settings.plugins[name] = settings;
    this.set(this.settings);
  }

  /**
   * Resets the settings for a specific plugin.
   *
   * @param {string} name Name of the plugin to reset settings for
   */
  resetPluginSettings(name: string): void {
    this.settings.plugins[name] = {};
    this.set(this.settings);
  }
}
